/**
 * Minimal `navigator.locks` surface this module needs, so the ordering rule
 * below is unit-testable without a browser.
 */
export interface LockManagerLike {
  request(name: string, callback: () => Promise<never>): Promise<unknown>;
}

/**
 * Hold `name` for as long as this page lives, and run `onHeld` only once the
 * lock has actually been **granted** (见 18 的 liveness 握手).
 *
 * The ordering is the whole point. The worker requests this same name to learn
 * when the page dies, so a page that publishes the name before its own request
 * is granted loses a race it cannot see: the worker's request reaches the lock
 * manager first, acquires immediately, and the host concludes the page is
 * already gone and drops the connection. Callers must therefore send the name
 * from `onHeld`, never alongside the request.
 */
export function holdConnectionLock(
  locks: LockManagerLike,
  name: string,
  onHeld: () => void,
): void {
  void locks
    .request(name, () => {
      onHeld();
      // Never resolves: the browser releases the lock when the page dies.
      return new Promise<never>(() => undefined);
    })
    .catch(() => {
      // Without a lock there is no liveness signal and the host falls back to
      // `bye`; announce anyway, or this page would never connect at all.
      onHeld();
    });
}
